import { z } from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { agentPersonaIdSchema } from '../../common/agent.schema';
import {
  conversationChannelTypeSchema,
  type ConversationChannelType,
} from '../../common/channel.schema';
import { languageSchema, languageSourceSchema } from '../../common/language.schema';
import { dateSchema, idSchema, nameSchema, objectIdSchema, phoneSchema } from '../../validation';
import { emailSchema } from '../../validation/email';

export { conversationChannelTypeSchema, type ConversationChannelType };

/**
 * Things a user may tell us that a later step depends on.
 *
 * A closed list so a tool can declare which ones it needs and the brief
 * can name them. Add a key when a write needs a new answer "in their own
 * words" — never a second, per-feature check of the transcript.
 */
export const statedFactKeySchema = z.enum([
  'account_type',
  'truck_type',
  'lane',
  'cargo',
  'company_name',
  'driver_phone',
  'monthly_orders',
]);
export type StatedFactKey = z.infer<typeof statedFactKeySchema>;

/**
 * One thing the user told us, in their words.
 *
 * Written by the model through `record_stated_fact` on the turn they say
 * it; code checks only that `quote` is in that turn's messages. A newer
 * entry for the same key replaces the older one, so a correction wins
 * however long ago the first answer was.
 */
export const conversationStatedFactSchema = z.object({
  key: statedFactKeySchema,
  /** Normalised value, e.g. `freelancer_driver` for `account_type`. */
  value: z.string().min(1),
  /** Their words, as sent. */
  quote: z.string().min(1),
  /** The user message the quote was found in. */
  messageId: objectIdSchema.optional(),
  at: dateSchema,
});
export type ConversationStatedFact = z.infer<typeof conversationStatedFactSchema>;

/**
 * Where a {@link ConversationSchema} thread lives.
 *
 * - `in_app` — dashboard / app chat; no connected account.
 * - Everything else — optional `channelAccountId` points at
 *   {@link ChannelAccountSchema} (required for new WhatsApp / email
 *   threads; legacy rows may omit it until the next inbound backfill).
 */
export const conversationChannelSchema = z
  .object({
    type: conversationChannelTypeSchema,
    channelAccountId: objectIdSchema.optional(),
  })
  .superRefine((val, ctx) => {
    if (val.type === 'in_app' && val.channelAccountId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'in_app conversations must not carry channelAccountId',
        path: ['channelAccountId'],
      });
    }
  });
export type ConversationChannel = z.infer<typeof conversationChannelSchema>;

/** Read the surface type from a nested conversation channel. */
export const getConversationChannelType = (
  channel: ConversationChannel,
): ConversationChannelType => channel.type;

/**
 * Who replies to inbound messages on this thread:
 *
 * - `ai`     — Hero AI auto-replies (MVP default).
 * - `human`  — A Hero staff or org user has taken over; AI stays out of
 *              the way until flipped back.
 * - `hybrid` — AI drafts, human approves before send. Phase 4+.
 */
export const conversationResponderModeSchema = z.enum(['ai', 'human', 'hybrid']);
export type ConversationResponderMode = z.infer<typeof conversationResponderModeSchema>;

/**
 * An action this thread asked for and the paywall held, waiting on a
 * payment.
 *
 * Deliberately thin — it records *that* something is waiting, not what.
 * The action itself is still sitting in the transcript as a live
 * `confirmation_card`, because the gate runs before the card is claimed
 * precisely so the user is not left holding a spent token. Copying the
 * payload here would create a second source of truth for the same
 * order, and the two would disagree the moment the user amended the
 * card while the invoice was open.
 *
 * Lives on the conversation rather than the Org because the resume has
 * to answer *somewhere*, and the thread is the only thing that knows
 * which number is waiting. An Org paying from the dashboard with no
 * hold anywhere simply has nothing to resume.
 */
export const conversationPaywallHoldSchema = z.object({
  /** Which gate fired — see `EntitlementGatePoint` in `@hero/core`. */
  gatePoint: z.enum(['account_created', 'before_order_commit', 'before_waybill_issue']),
  heldAt: dateSchema,
});
export type ConversationPaywallHold = z.infer<typeof conversationPaywallHoldSchema>;

/**
 * The one-question subscription offer sent after a thread's first
 * government وثيقة, and what happened to it.
 *
 * It exists because the answer is a bare "نعم" — the least specific
 * message a thread can receive, and the same word that confirms a
 * staged card. Without a record of an outstanding question, that word
 * has to be guessed at from the transcript: it would either commit
 * whatever card was last on screen or be read as ordinary chat. Holding
 * the ask here means the reply is routed to the thing that asked.
 *
 * `answeredAt` is stamped by the **first** inbound after the ask,
 * whatever it says. An offer that stays outstanding is an offer that can
 * hijack a "نعم" days later, which is exactly the confusion this field
 * was added to prevent; a reply about something else consumes it as
 * `unrelated` and the ask never fires again.
 *
 * `reasonAskedAt` carries the follow-up: a decline is worth one "why?",
 * and the answer to it goes on the Lead as an objection (see
 * `leadObjectionSchema`) rather than staying here, because what a
 * prospect refuses to buy outlives the thread they refused it on.
 */
export const conversationSubscribeOfferSchema = z.object({
  askedAt: dateSchema,
  /**
   * The `ConversationMessage` that carries the ask. A WhatsApp button tap
   * quotes the bubble it answers (`context.id` → that row's
   * `externalMessageId`), so a tap on THIS bubble is the offer's answer
   * even if an unrelated message arrived in between — «وين الوثيقة» two
   * minutes earlier used to close the offer, and the later «نعم» tap on
   * it then reached the model as a bare yes (runtime, 17 Sep 2026).
   */
  messageId: objectIdSchema.optional(),
  /** When the thread replied to it. Absent while the question is live. */
  answeredAt: dateSchema.optional(),
  answer: z.enum(['accepted', 'declined', 'unrelated']).optional(),
  /** Set when a decline has been asked for its reason. */
  reasonAskedAt: dateSchema.optional(),
  /** When the reason landed — see `LeadService.recordObjection`. */
  reasonAt: dateSchema.optional(),
});
export type ConversationSubscribeOffer = z.infer<typeof conversationSubscribeOfferSchema>;

/**
 * Lifecycle status of the thread.
 *
 * - `active`            — open, AI or human is replying.
 * - `waiting_response`  — we're waiting for the other side.
 * - `escalated`         — flipped to a human via `escalate_to_human`.
 * - `closed`            — no further activity expected.
 */
export const conversationStatusSchema = z.enum([
  'active',
  'waiting_response',
  'escalated',
  'closed',
]);
export type ConversationStatus = z.infer<typeof conversationStatusSchema>;

/**
 * Who participates in the thread. Multi-valued so a single thread can
 * carry the platform user (carrier dispatcher) + the external party
 * (driver on WhatsApp) + linked Client / Contact refs for CRM context.
 *
 * `type` discriminates how to interpret the rest:
 * - `platform_user` — authenticated Hero user; `userId` set
 * - `client`        — CRM Client doc; `clientId` set
 * - `contact`       — CRM Contact doc; `contactId` set
 * - `driver`        — org Driver row without a platform account
 *                     (`Driver.userId` unset); `driverId` + `phone` set.
 *                     The driver-without-app WhatsApp flow — see
 *                     `docs/ai/whatsapp-agent.md`.
 * - `external`      — phone or email outside the platform
 * - `staff`         — Hero internal staff; `userId` set (AdminUser)
 */
export const conversationParticipantSchema = z.object({
  type: z.enum(['platform_user', 'client', 'contact', 'driver', 'external', 'staff']),
  userId: objectIdSchema.optional(),
  clientId: objectIdSchema.optional(),
  contactId: objectIdSchema.optional(),
  driverId: objectIdSchema.optional(),
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
  displayName: nameSchema.optional(),
});
export type ConversationParticipant = z.infer<typeof conversationParticipantSchema>;

/**
 * The per-conversation lock — see `docs/ai/concurrency.md`.
 *
 * `findOneAndUpdate` claim with `expiresAt` lease (60s default) and a
 * stale-lock sweeper. The lease is renewed every 20s while a long
 * graph turn is running; if renewal fails (another instance reclaimed),
 * the run aborts cleanly.
 */
export const conversationLockSchema = z.object({
  runId: objectIdSchema,
  expiresAt: dateSchema,
});
export type ConversationLock = z.infer<typeof conversationLockSchema>;

/**
 * A message fragment that arrived while another replica held
 * {@link conversationLockSchema} on this thread and the running turn had
 * already reached its first model call (so it must not be aborted).
 *
 * The inbound message itself is already on the transcript; this is only
 * the *pointer* the lock owner drains at release time so one follow-up
 * turn answers every fragment that queued behind it. The queue lives on
 * the conversation, not in process memory, because the burst fan-out
 * across Cloud Run replicas is exactly the case where in-memory state
 * fails — see `docs/ai/concurrency.md`.
 *
 * Attachment shape mirrors `conversationAttachmentSchema` minus the
 * storage / transcript detail (the durable row already holds those);
 * the reader only needs `type` + `url` to re-run vision.
 */
export const conversationPendingInboundSchema = z.object({
  text: z.string(),
  attachments: z
    .array(
      z.object({
        type: z.enum(['image', 'pdf', 'audio', 'video', 'file']),
        url: z.string().url(),
        mimeType: idSchema.optional(),
        pages: z.number().int().positive().optional(),
        name: nameSchema.optional(),
      }),
    )
    .default([]),
  receivedAt: dateSchema,
  /** Provider message id (WhatsApp `wamid.…`) of the queued fragment. */
  externalMessageId: idSchema.optional(),
});
export type ConversationPendingInbound = z.infer<typeof conversationPendingInboundSchema>;

/**
 * The per-thread **outbound** lease — see `docs/ai/concurrency.md`.
 *
 * Distinct from {@link conversationLockSchema}, which guards the
 * LangGraph run. Different lifetimes and different holders: a template
 * send takes no graph lock, and an inbound turn holds this one for the
 * whole webhook request (including the part after the graph has
 * released its lock) because the reply is still unsent until the HTTP
 * response goes back.
 *
 * `holderId` is a fresh token per claim rather than a run id, so renew
 * and release can compare-and-set on it: a replica whose lease expired
 * and was reclaimed by a peer must not be able to extend or clear the
 * peer's lease.
 */
export const conversationLeaseSchema = z.object({
  holderId: idSchema,
  /** What took it. Diagnostics only — both kinds are exclusive. */
  reason: z.enum(['inbound_turn', 'drain']),
  expiresAt: dateSchema,
});
export type ConversationLease = z.infer<typeof conversationLeaseSchema>;

/**
 * The Buraq conversation's lifecycle on a WhatsApp thread.
 *
 * Buraq's unit of work is a flow per phone, and the conversation it
 * opens keeps routing that number into the flow that opened it. Nothing
 * closed it before this, so a reply sent a day after a template never
 * reached our inbound webhook at all — see `docs/ai/whatsapp-agent.md`.
 *
 * There is deliberately **no pending counter** here:
 * `Conversation.pendingOutbound.length` is the truth, and a second copy
 * of it is a second thing to get wrong.
 */
export const conversationWhatsAppSessionSchema = z.object({
  /**
   * When the last bot flow was started for this thread. Durable rather
   * than per-process so two replicas space themselves from the same
   * clock — an in-memory map lets both start a flow at once, which is
   * exactly the collision the spacing exists to prevent.
   */
  lastOutboundAt: dateSchema.optional(),
  /**
   * Earliest time the burst may be closed. Re-armed by every settle, so
   * a template chained behind a reply extends the deadline instead of
   * racing the close.
   */
  closeAfter: dateSchema.optional(),
  /**
   * When the last CLOSE was posted. A `closedAt` older than
   * `closeAfter` means a new burst has been armed since; that
   * comparison is what makes the close claim idempotent across
   * replicas without a second lock.
   */
  closedAt: dateSchema.optional(),
});
export type ConversationWhatsAppSession = z.infer<typeof conversationWhatsAppSessionSchema>;

/**
 * Thread-level context — *"this conversation is about order X"*.
 * Optional. Used by widgets / inbox UI to deep-link the right entity
 * drawer. The same conversation can stay scoped to one entity for its
 * whole life, or hop between entities as the conversation evolves.
 */
export const conversationContextSchema = z.object({
  orderId: objectIdSchema.optional(),
  clientId: objectIdSchema.optional(),
  contactId: objectIdSchema.optional(),
  driverId: objectIdSchema.optional(),
});
export type ConversationContext = z.infer<typeof conversationContextSchema>;

/** One fact the pre-model resolver read from a user message, with where it came from. */
const conversationMemorySourceSchema = z.object({
  /** The `ConversationMessage` the fact came from. */
  sourceMessageId: objectIdSchema.optional(),
  at: dateSchema,
});

export const conversationMemoryDriverCandidateSchema = z.object({
  driverId: objectIdSchema,
  name: nameSchema.optional(),
  plate: z.string().optional(),
});

/**
 * The order the user is building across messages — a lane in one
 * message, the driver's name in the next. Written by code before the
 * model runs (`ConversationMemoryService`), rendered in the brief, and
 * cleared when a create card is committed. Never copy: facts only.
 */
export const conversationMemoryDraftSchema = z.object({
  lane: conversationMemorySourceSchema
    .extend({
      /** City names as the user typed them, in order. */
      origin: z.string().optional(),
      destinations: z.array(z.string()).default([]),
    })
    .optional(),
  driver: conversationMemorySourceSchema
    .extend({
      /** The mention, verbatim. */
      query: z.string(),
      status: z.enum(['resolved', 'choose', 'new_hire', 'not_found']),
      driverId: objectIdSchema.optional(),
      name: nameSchema.optional(),
      plate: z.string().optional(),
      candidates: z.array(conversationMemoryDriverCandidateSchema).optional(),
    })
    .optional(),
  /** What is being shipped, as the user typed it («كرتون»). */
  cargo: conversationMemorySourceSchema.extend({ value: z.string() }).optional(),
  /** Saudi mobiles typed in the thread (canonical `9665XXXXXXXX`). */
  phones: z.array(conversationMemorySourceSchema.extend({ value: z.string() })).optional(),
  /** National ID / iqama numbers typed in the thread. */
  identityNumbers: z.array(conversationMemorySourceSchema.extend({ value: z.string() })).optional(),
  /** 700-numbers (unified establishment numbers) typed in the thread. */
  unifiedNumbers: z.array(conversationMemorySourceSchema.extend({ value: z.string() })).optional(),
  updatedAt: dateSchema,
});
export type ConversationMemoryDraft = z.infer<typeof conversationMemoryDraftSchema>;

export const fleetIntakeStatusSchema = z.enum([
  'ready',
  'needs_phone',
  /** Known only by the istimara's هوية المستخدم — no iqama, no name yet. */
  'needs_name',
  'needs_card',
  'unpaired',
  'saved',
]);
export type FleetIntakeStatus = z.infer<typeof fleetIntakeStatusSchema>;

/** One driver and/or truck read off the cards the user sent, across turns. */
export const fleetIntakeEntrySchema = z.object({
  /** Stable across rebuilds: the person's ID number, else name, else plate. */
  key: z.string(),
  person: z
    .object({
      name: z.string().optional(),
      identityNumber: z.string().optional(),
    })
    .optional(),
  truck: z.object({ plate: z.string() }).optional(),
  phone: z.string().optional(),
  /** How the truck was put with the person — never guessed beyond these. `user_stated`: they said which truck is his. */
  pairedBy: z.enum(['operator_id', 'same_file', 'same_burst', 'user_stated']).optional(),
  status: fleetIntakeStatusSchema,
  sourceMessageIds: z.array(z.string()).default([]),
});
export type FleetIntakeEntry = z.infer<typeof fleetIntakeEntrySchema>;

export const conversationMemorySchema = z.object({
  draft: conversationMemoryDraftSchema.optional(),
  fleetIntake: z
    .object({
      entries: z.array(fleetIntakeEntrySchema).default([]),
      updatedAt: dateSchema,
    })
    .optional(),
});
export type ConversationMemory = z.infer<typeof conversationMemorySchema>;

/**
 * `Conversation` — one inbox thread.
 *
 * Per-channel-thread:
 * - `in_app`: `(participants.userId, org?)` — one thread per
 *   (user, org) for org members; per user for org-less callers.
 * - `whatsapp`: `(channel.channelAccountId, externalThreadId)` unique
 *   (partial on `externalThreadId`) — the same customer phone talking
 *   to two different org numbers is two threads.
 * - `email`: `Message-Id` thread root.
 *
 * Ownership / filtering model (mirrored on `ConversationMessage`):
 * - **by org** — `'org._id'` (only ever a REAL Org).
 * - **by user** — `'participants.userId'`.
 * - **by user in org** — both filters combined.
 * Guest threads (unknown WhatsApp phone → `hero_sales`) have NO `org`;
 * they're scoped by `channel.channelAccountId` + `externalThreadId`.
 *
 * The schema is channel-complete so adding a new adapter (voice /
 * email) is a message-module change, not a migration.
 *
 * See `docs/ai/messaging-model.md` and `docs/ai/schemas.md`.
 */
export const ConversationSchema = createModel({
  /**
   * Owning tenant — ALWAYS a real Org (`target: 'org'`). Absent for
   * guest threads and org-less individual users; never a synthetic
   * "Anonymous" user party (pre-Jul-2026 rows did that — the
   * collection was reset at the cutover). The human link lives in
   * `participants[]`, not here.
   */
  org: partySchema
    .refine(p => p.target === 'org', {
      message: 'Conversation.org must reference a real Org (target "org")',
    })
    .optional(),

  channel: conversationChannelSchema,
  externalThreadId: idSchema.optional(),

  participants: z.array(conversationParticipantSchema).default([]),
  subject: nameSchema.optional(),
  context: conversationContextSchema.optional(),

  responderMode: conversationResponderModeSchema.default('ai'),
  /**
   * The staff member holding the thread while `responderMode` is
   * `human`. Cleared on hand-back. `assignedToName` is a display
   * snapshot so the inbox banner ("Sara has this thread") renders
   * without an AdminUser lookup.
   */
  assignedToUserId: objectIdSchema.optional(),
  assignedToName: nameSchema.optional(),
  /** When the current human takeover started. Cleared on hand-back. */
  takenOverAt: dateSchema.optional(),
  /**
   * The model's replay floor. `loadTranscript`, the pending-card
   * lookup and the order-card scan only read messages with
   * `_id >= replayFrom`; anything older is invisible to the model.
   *
   * Set on **hand-back** to the system bubble that records what staff
   * did, so the first thing the model replays is that note and nothing
   * before it — not the OCR extract with the wrong iqama, not the card
   * staged against the wrong driver, not the user's own typo. Admin
   * transcript reads ignore it; the cutoff is model-only. See
   * `docs/ai/human-takeover.md` § Poisoned conversations.
   */
  replayFrom: objectIdSchema.optional(),
  /**
   * Staff saved account / identity data during the current takeover.
   * Makes the default hand-back a reset (new `replayFrom`) — older
   * numbers in the thread no longer match the account. Cleared on
   * hand-back.
   */
  takeoverTouchedIdentity: z.boolean().optional(),
  status: conversationStatusSchema.default('active'),

  language: languageSchema.optional(),
  /**
   * What set {@link ConversationSchema.language}. Absent on legacy rows
   * that already carry a language — those rank as `typed`, so a short
   * name or lane can no longer flip them. See `docs/ai/multi-language.md`.
   */
  languageSource: languageSourceSchema.optional(),
  /**
   * Consecutive weak (`hint`) messages in one language. Two in a row
   * count as a typed sentence; any other evidence resets it.
   */
  languageHint: z
    .object({ language: languageSchema, count: z.number().int().nonnegative() })
    .optional(),
  /**
   * When the language picker went out. Sent at most once automatically;
   * only an explicit "change language" re-sends it.
   */
  languagePickerSentAt: dateSchema.optional(),

  /**
   * What the user told us, one entry per key. See
   * {@link conversationStatedFactSchema}. The brief renders it, and write
   * tools that need an answer in the user's words read it here instead of
   * searching the transcript.
   */
  statedFacts: z.array(conversationStatedFactSchema).optional(),

  /** What code resolved from the user's messages before the model ran. */
  memory: conversationMemorySchema.optional(),

  lastMessageAt: dateSchema,
  lastInboundAt: dateSchema.optional(),
  unreadCount: z.number().int().nonnegative().default(0),

  /**
   * Specialist intents the supervisor resolved on the LAST turn (e.g.
   * `['order', 'invoice']`). Used for sticky routing: a follow-up that
   * classifies as `chat` ("what about the second one?") re-mounts the
   * previous turn's tool partitions instead of dead-ending with no
   * tools. Refreshed every turn — carries at most one turn of history.
   */
  lastIntents: z.array(z.string()).optional(),

  /**
   * Persona resolved for this thread (e.g. `hero_customer_support`,
   * `hero_sales`). Stamped on creation by the runtime persona resolver
   * (channel + caller state) so every turn of the thread keeps the
   * same voice. Absent on pre-persona conversations → default persona.
   */
  personaId: agentPersonaIdSchema.optional(),

  /**
   * The CRM record this thread belongs to.
   *
   * Funnel progress used to live here, on the conversation, because the
   * funnel starts before an Org exists — the first steps happen while
   * the caller is still a guest. But a thread is not the person: the same
   * prospect moves from WhatsApp to the web, or starts a second thread
   * next month, and thread-scoped progress means each one starts from
   * zero. The `Lead` outlives every thread, so `Lead.funnels[]` holds the
   * progress and the conversation only points at it.
   *
   * Absent on threads that have no CRM record — an existing customer
   * asking a support question is not a lead.
   */
  leadId: objectIdSchema.optional(),

  /**
   * Set while an action on this thread is waiting on a subscription
   * payment; cleared when the payment lands and the action resumes.
   * See {@link conversationPaywallHoldSchema}.
   */
  paywallHold: conversationPaywallHoldSchema.optional(),

  /**
   * The post-وثيقة subscription offer this thread was sent, once. See
   * {@link conversationSubscribeOfferSchema}.
   */
  subscribeOffer: conversationSubscribeOfferSchema.optional(),

  lockedBy: conversationLockSchema.optional(),
  pendingMessages: z.array(objectIdSchema).default([]),
  /**
   * Fragments queued behind the current lock owner — see
   * {@link conversationPendingInboundSchema}. Pushed only while
   * `lockedBy` is live, drained atomically with the unlock.
   */
  pendingInbound: z.array(conversationPendingInboundSchema).optional(),

  /**
   * Serialises everything that can hold this thread's Buraq session —
   * an inbound turn that owes a synchronous reply, and each outbound
   * drain. See {@link conversationLeaseSchema}.
   *
   * **WhatsApp threads only.** In-app chat has no provider session to
   * hold (`InAppChannel` writes SSE frames straight to the response),
   * so it never takes the lease and pays nothing for any of this.
   */
  outboundLease: conversationLeaseSchema.optional(),

  /**
   * Templates awaiting their turn on this thread, oldest-first. Each
   * entry is a `ConversationMessage._id` whose `outbound` carries the
   * send instruction. Usually empty — a template only queues when the
   * thread is busy.
   *
   * Deliberately NOT {@link ConversationSchema.pendingMessages}, which
   * `docs/ai/concurrency.md` documents for the *inbound* `queue`
   * policy. Putting an inbound and an outbound queue in one array would
   * make both unreadable.
   */
  pendingOutbound: z.array(objectIdSchema).default([]),

  /**
   * Buraq conversation lifecycle for this thread. WhatsApp only. See
   * {@link conversationWhatsAppSessionSchema}.
   */
  whatsappSession: conversationWhatsAppSessionSchema.optional(),
});

export type Conversation = z.infer<typeof ConversationSchema.model>;
export type ConversationData = z.infer<typeof ConversationSchema.data>;
export type ConversationPartial = z.infer<typeof ConversationSchema.partial>;
export type ConversationQueryOne = QueryOne<Conversation>;
export type ConversationQueryMany = QueryMany<Conversation>;
